/**
 * @fileoverview CommandPalette context + hook
 *
 * Split out of `CommandPalette.tsx` (react-refresh/only-export-components) —
 * lets any consumer (sidebar Search button, future shortcut UIs) open /
 * close / toggle the palette without prop-drilling state through the layout
 * tree. `CommandPalette` itself reads its open state from here instead of
 * owning local state, so context and modal stay in lockstep.
 */

import { createContext, useContext } from "react";

export interface CommandPaletteContextValue {
  open: boolean;
  setOpen: (next: boolean) => void;
  openPalette: () => void;
  closePalette: () => void;
  toggle: () => void;
}

export const CommandPaletteContext =
  createContext<CommandPaletteContextValue | null>(null);

/**
 * Read/control the command-palette open state. Returns a no-op
 * fallback if no provider is mounted so consumers in detached trees
 * (Storybook etc.) don't crash.
 */
export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (ctx) return ctx;
  const noop = () => {};
  return {
    open: false,
    setOpen: noop,
    openPalette: noop,
    closePalette: noop,
    toggle: noop,
  };
}
